import { defaultStoreUtils } from '@databases/redux';
import { selectValueSingle } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { getValueId } from '@databases/utils/utils';
import React, { useEffect, useState } from 'react';

import { CommentThreadEvent } from '@canvas/comments/comment/store';

import { OpenThread } from '../context/CommentContext';
import useCommentContext from '../hooks/useCommentContext';

import CommentThread, { Thread } from './CommentThread';

interface CommentThreadProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  commentToggled: boolean;
  threads: Thread[] | undefined;
  openThread: OpenThread;
  create?: boolean;
  setIsDeleting: (isDeleting: boolean) => void;
  handleClose: () => void;
}

const CommentThreads = ({
  contentId,
  fieldId,
  entryId,
  commentToggled,
  threads,
  openThread,
  create,
  setIsDeleting,
  handleClose,
}: CommentThreadProps) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { setOpenThread } = useCommentContext();
  const focusedThreadIndex =
    openThread.threadId && threads
      ? threads.findIndex((thread) => thread?.threadId === openThread.threadId)
      : undefined;
  const [selectedThreadIndex, setSelectedThreadIndex] = useState<number>(
    focusedThreadIndex && focusedThreadIndex !== -1 ? focusedThreadIndex : 0
  );
  const [selectedThread, setSelectedThread] = useState<Thread>(undefined);
  const [isCreate, setIsCreate] = useState(!!create);
  const [currentUserId, stringRepresentation] = defaultStoreUtils.useEqualSelector(
    (state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const field = getDatabaseFieldRequired(database, fieldId);
      const entry = getDatabaseEntryRequired(database, entryId);
      const value = getDatabaseValue(database, field, entry);
      const currentUserId = selectCurrentUserId(state);

      const stringRepresentation =
        value.type !== DatabaseFieldType.ATTACHMENT
          ? getStringRepresentation({ database, field, entry, value }, state)
          : undefined;

      return [currentUserId, stringRepresentation] as const;
    }
  );

  useEffect(() => {
    dispatch(
      selectValueSingle({
        entryId: entryId,
        fieldId: fieldId,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!create && threads && threads.length) {
      setSelectedThread(threads[selectedThreadIndex]);
    } else {
      setSelectedThread(undefined);
      setIsCreate(true);
    }
  }, [create, dispatch, entryId, fieldId, isCreate, selectedThreadIndex, threads]);

  const onNextThread = () => {
    if (threads && threads.length > selectedThreadIndex + 1) {
      setSelectedThreadIndex((prevIndex) => prevIndex + 1);
    }
  };

  const onPreviousThread = () => {
    if (selectedThreadIndex > 0) {
      setSelectedThreadIndex((prevIndex) => prevIndex - 1);
    }
  };

  const onThreadCreate = (evt: CommentThreadEvent) => {
    if (evt.type === 'threadCreated') {
      setIsCreate(false);
      setOpenThread({
        valueId: getValueId(entryId, fieldId),
        create: false,
        panel: openThread.panel,
      });
      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.ADD_VALUE_COMMENT_THREAD,
          entryId,
          fieldId,
          newThread: {
            threadId: evt.externalId,
            isResolved: false,
            reactionsId: undefined,
            commentCount: 1,
            versionNumber: 0,
          },
        })
      );

      if (threads) {
        setSelectedThreadIndex(threads.length);
      } else {
        setSelectedThreadIndex(0);
      }
    }
  };

  return (
    <CommentThread
      contentId={contentId}
      fieldId={fieldId}
      entryId={entryId}
      commentToggled={commentToggled}
      thread={selectedThread}
      currentUserId={currentUserId}
      isCreate={isCreate}
      stringRepresentation={stringRepresentation}
      setIsDeleting={setIsDeleting}
      onThreadCreate={onThreadCreate}
      threadNavigationOpts={
        threads?.length && !create
          ? {
              currentThreadNumber: selectedThreadIndex + 1,
              threadCount: threads.length,
              onNextThread,
              onPreviousThread,
            }
          : undefined
      }
      handleClose={handleClose}
    />
  );
};

export default CommentThreads;
